import { ConfirmationResult, PhoneAuthProvider, RecaptchaVerifier, signInWithCredential, signInWithPhoneNumber, UserCredential } from "firebase/auth";
import { getFirebaseAuth } from "./firebase.client";

// Global RecaptchaVerifier instance - reused across OTP calls
let firebaseRecaptchaVerifier: RecaptchaVerifier | null = null;

// Captured reCAPTCHA token from the invisible verifier callback
let capturedRecaptchaToken: string | null = null;
export const getCapturedRecaptchaToken = () => capturedRecaptchaToken;
export const clearCapturedRecaptchaToken = () => {
  capturedRecaptchaToken = null;
};

// Create or retrieve the Firebase RecaptchaVerifier instance
export const getOrCreateRecaptchaVerifier = (): RecaptchaVerifier => {
  if (typeof window === "undefined") {
    throw new Error("RecaptchaVerifier only works in browser environment");
  }

  // If verifier already exists, return it
  if (firebaseRecaptchaVerifier) {
    return firebaseRecaptchaVerifier;
  }

  const auth = getFirebaseAuth();
  const containerId = "firebase-recaptcha-container";

  // Ensure DOM container exists and is clean
  let container = document.getElementById(containerId);
  if (container) {
    // Clear any existing content to avoid "already rendered" error
    container.innerHTML = "";
  } else {
    container = document.createElement("div");
    container.id = containerId;
    container.style.display = "none";
    document.body.appendChild(container);
  }

  console.log("Creating RecaptchaVerifier with container:", containerId);
  firebaseRecaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: (response: unknown) => {
      console.log("✅ reCAPTCHA verified:", response);
    },
    "expired-callback": () => {
      console.log("⚠️ reCAPTCHA expired - will refresh on next use");
      // Don't set to null - Firebase will handle refresh automatically
    },
  });

  return firebaseRecaptchaVerifier;
};

// Clear the verifier and remove the DOM container entirely
export const clearRecaptchaVerifier = () => {
  if (firebaseRecaptchaVerifier) {
    try {
      (firebaseRecaptchaVerifier as any).clear?.();
    } catch (_) {}
    firebaseRecaptchaVerifier = null;
  }
  const container = document.getElementById("firebase-recaptcha-container");
  if (container) container.remove();
};

// Setup Firebase reCAPTCHA verifier (kept for backward compatibility)
export const setupRecaptcha = async (containerId: string = "recaptcha-container"): Promise<RecaptchaVerifier | null> => {
  if (typeof window === "undefined") {
    console.warn("setupRecaptcha called on server side");
    return null;
  }

  try {
    return getOrCreateRecaptchaVerifier();
  } catch (error) {
    console.error("Error setting up reCAPTCHA:", error);
    return null;
  }
};

/**
 * Send OTP — follows the same pattern as temp-login:
 * create a fresh invisible RecaptchaVerifier each call, clear on error.
 */
export const sendOTPWithToken = async (phoneNumber: string, _recaptchaToken?: string): Promise<ConfirmationResult | null> => {
  // Always start with a clean slate (avoids "already rendered" errors)
  clearRecaptchaVerifier();

  const auth = getFirebaseAuth();
  const containerId = "firebase-recaptcha-container";

  // Re-create the container div fresh
  const container = document.createElement("div");
  container.id = containerId;
  document.body.appendChild(container);

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: (token: string) => {
      capturedRecaptchaToken = token;
    },
  });
  firebaseRecaptchaVerifier = verifier;

  const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, verifier);
    return confirmationResult;
  } catch (error: unknown) {
    // Clean up so the next attempt can start fresh
    clearRecaptchaVerifier();

    const firebaseError = error as { code?: string; message?: string };
    if (firebaseError.code === "auth/internal-error") {
      throw new Error(
        "Firebase Phone Authentication is not enabled. Go to Firebase Console → Authentication → Sign-in method → Enable Phone provider",
      );
    } else if (firebaseError.code === "auth/invalid-phone-number") {
      throw new Error("Invalid phone number format");
    } else if (firebaseError.code === "auth/too-many-requests") {
      throw new Error("Too many requests. Please try again later");
    } else if (firebaseError.code === "auth/quota-exceeded") {
      throw new Error("SMS quota exceeded. Please try again later");
    } else if (firebaseError.code === "auth/captcha-check-failed") {
      throw new Error("reCAPTCHA verification failed. Please try again");
    } else if (firebaseError.code === "auth/missing-phone-number") {
      throw new Error("Phone number is required");
    } else if (firebaseError.code === "auth/invalid-app-credential") {
      throw new Error("Firebase configuration error. Please check your Firebase setup");
    } else if (firebaseError.code === "auth/argument-error") {
      throw new Error("Invalid phone number format. Please use correct number for the selected country");
    }
    throw new Error(firebaseError.message || "Failed to send OTP. Please try again");
  }
};
export const sendOTP = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<ConfirmationResult | null> => {
  try {
    const auth = await getFirebaseAuth();
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    return confirmationResult;
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };

    if (firebaseError.code === "auth/internal-error") {
      throw new Error(
        "Firebase Phone Authentication is not enabled. Go to Firebase Console → Authentication → Sign-in method → Enable Phone provider",
      );
    } else if (firebaseError.code === "auth/invalid-phone-number") {
      throw new Error("Invalid phone number format");
    } else if (firebaseError.code === "auth/too-many-requests") {
      throw new Error("Too many requests. Please try again later");
    } else if (firebaseError.code === "auth/quota-exceeded") {
      throw new Error("SMS quota exceeded. Please try again later");
    } else if (firebaseError.code === "auth/captcha-check-failed") {
      throw new Error("reCAPTCHA verification failed. Please try again");
    } else if (firebaseError.code === "auth/missing-phone-number") {
      throw new Error("Phone number is required");
    } else if (firebaseError.code === "auth/invalid-app-credential") {
      throw new Error("Firebase configuration error. Please check your Firebase setup");
    }

    // Return more descriptive error
    throw new Error(firebaseError.message || "Failed to send OTP. Please try again");
  }
};

// Verify OTP code
export const verifyOTP = async (confirmationResult: ConfirmationResult, otp: string): Promise<UserCredential | null> => {
  try {
    const result = await confirmationResult.confirm(otp);

    clearRecaptchaVerifier();
    return result;
  } catch (error: unknown) {
    console.error("Error verifying OTP:", error);

    const firebaseError = error as { code?: string };
    // Handle specific errors
    if (firebaseError.code === "auth/invalid-verification-code") {
      throw new Error("Invalid OTP code");
    } else if (firebaseError.code === "auth/code-expired") {
      throw new Error("OTP code has expired");
    }

    throw error;
  }
};

// Sign in with phone credential
// export const signInWithPhoneCredential = async (verificationId: string, verificationCode: string): Promise<UserCredential | null> => {
//   try {
//     const auth = await getFirebaseAuth();
//     const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
//     const result = await signInWithCredential(auth, credential);
//     return result;
//   } catch (error) {
//     console.error("Error signing in with phone credential:", error);
//     throw error;
//   }
// };

/**
 * Resend OTP — same fresh-verifier pattern as sendOTPWithToken.
 */
export const resendOTPWithToken = async (phoneNumber: string, _recaptchaToken?: string): Promise<ConfirmationResult | null> => {
  clearRecaptchaVerifier();

  const auth = getFirebaseAuth();
  const containerId = "firebase-recaptcha-container";

  const container = document.createElement("div");
  container.id = containerId;
  document.body.appendChild(container);

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: (token: string) => {
      capturedRecaptchaToken = token;
    },
  });
  firebaseRecaptchaVerifier = verifier;

  const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, verifier);
    return confirmationResult;
  } catch (error: unknown) {
    clearRecaptchaVerifier();

    const firebaseError = error as { code?: string; message?: string };
    if (firebaseError.code === "auth/internal-error") {
      throw new Error(
        "Firebase Phone Authentication is not enabled. Go to Firebase Console → Authentication → Sign-in method → Enable Phone provider",
      );
    } else if (firebaseError.code === "auth/invalid-phone-number") {
      throw new Error("Invalid phone number format");
    } else if (firebaseError.code === "auth/too-many-requests") {
      throw new Error("Too many resend requests. Please try again later");
    } else if (firebaseError.code === "auth/quota-exceeded") {
      throw new Error("SMS quota exceeded. Please try again later");
    } else if (firebaseError.code === "auth/captcha-check-failed") {
      throw new Error("reCAPTCHA verification failed. Please try again");
    } else if (firebaseError.code === "auth/missing-phone-number") {
      throw new Error("Phone number is required");
    } else if (firebaseError.code === "auth/invalid-app-credential") {
      throw new Error("Firebase configuration error. Please check your Firebase setup");
    } else if (firebaseError.code === "auth/argument-error") {
      throw new Error("Invalid phone number format. Please use correct number for the selected country");
    }
    throw new Error(firebaseError.message || "Failed to resend OTP. Please try again");
  }
};

/**
 * Resend OTP to phone number (LEGACY - for backward compatibility)
 */
export const resendOTP = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<ConfirmationResult | null> => {
  try {
    const auth = await getFirebaseAuth();

    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    return confirmationResult;
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };

    if (firebaseError.code === "auth/internal-error") {
      throw new Error(
        "Firebase Phone Authentication is not enabled. Go to Firebase Console → Authentication → Sign-in method → Enable Phone provider",
      );
    } else if (firebaseError.code === "auth/invalid-phone-number") {
      throw new Error("Invalid phone number format");
    } else if (firebaseError.code === "auth/too-many-requests") {
      throw new Error("Too many resend requests. Please try again later");
    } else if (firebaseError.code === "auth/quota-exceeded") {
      throw new Error("SMS quota exceeded. Please try again later");
    } else if (firebaseError.code === "auth/captcha-check-failed") {
      throw new Error("reCAPTCHA verification failed. Please try again");
    } else if (firebaseError.code === "auth/missing-phone-number") {
      throw new Error("Phone number is required");
    } else if (firebaseError.code === "auth/invalid-app-credential") {
      throw new Error("Firebase configuration error. Please check your Firebase setup");
    }

    // Return more descriptive error
    throw new Error(firebaseError.message || "Failed to resend OTP. Please try again");
  }
};

/**
 * Get current Firebase user
 */
export const getCurrentFirebaseUser = async () => {
  try {
    const auth = await getFirebaseAuth();
    return auth.currentUser || null;
  } catch (error) {
    console.warn("Firebase auth not initialized");
    return null;
  }
};

/**
 * Sign out from Firebase
 */
export const signOutFirebase = async () => {
  try {
    const auth = await getFirebaseAuth();
    await auth.signOut();
    console.log("Firebase sign out successful");
  } catch (error) {
    console.error("Error signing out from Firebase:", error);
    throw error;
  }
};

/**
 * Get Firebase ID token
 */
export const getFirebaseIdToken = async (): Promise<string | null> => {
  const user = await getCurrentFirebaseUser();
  if (!user) return null;

  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error("Error getting Firebase ID token:", error);
    return null;
  }
};
